import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Download, MoreHorizontal, Edit, Eye, Trash2, Settings } from 'lucide-react';
import { Store as StoreType, StorePointsSettingsData } from '../types';
import Swal from 'sweetalert2';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'outline' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => props.$variant === 'primary' ? `
    background: #00d4aa;
    color: white;
    border: none;

    &:hover {
      background: #00b894;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    }
  ` : `
    background: white;
    color: #00d4aa;
    border: 2px solid #00d4aa;

    &:hover {
      background: #00d4aa;
      color: white;
    }
  `}

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const TableWrapper = styled.div`
  max-height: 600px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 720px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 16px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #f8f9fa;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #f8f9fa;
  color: #666;
  vertical-align: middle;
`;

const StoreName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const StoreInfo = styled.div`
  font-size: 13px;
  color: #999;
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const ActionMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const ActionButton = styled.button`
  padding: 8px;
  border-radius: 6px;
  color: #666;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f8f9fa;
    color: #333;
  }
`;

const DropdownMenu = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'block' : 'none'};
  position: absolute;
  right: 0;
  bottom: 100%;
  margin-bottom: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  min-width: 180px;
  z-index: 1000;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  color: #333;
  font-size: 14px;
  transition: background-color 0.2s ease;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &.danger {
    color: #dc3545;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #f8f9fa;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: #00d4aa;
    color: white;
    font-weight: 600;
  ` : `
    background: white;
    color: #666;
    border: 1px solid #dee2e6;

    &:hover {
      background: #f8f9fa;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  color: #666;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ITEMS_PER_PAGE = 10;

const SelectInput = styled.select`
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

export const StoresPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsStore, setPointsStore] = useState<StoreType | null>(null);
  const [pointsSettings, setPointsSettings] = useState<StorePointsSettingsData>({
    points_per_currency: 1,
    min_purchase_amount: null,
    points_rounding: 'round',
  });

  const { data: stores, isLoading, error } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await apiService.getStores();
      return response.data;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiService.deleteStore(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Loja excluída com sucesso',
        showConfirmButton: false,
        timer: 2000,
        customClass: {
          popup: 'animated fadeInDown'
        }
      });
    },
    onError: () => {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Não foi possível excluir a loja',
        confirmButtonColor: '#00d4aa'
      });
    }
  });

  const pointsSettingsMutation = useMutation({
    mutationFn: async ({ storeId, data }: { storeId: number; data: StorePointsSettingsData }) => {
      return apiService.updateStorePointsSettings(storeId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      setShowPointsModal(false);
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Configurações de pontos salvas',
        showConfirmButton: false,
        timer: 2000,
      });
    },
    onError: () => {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível salvar as configurações',
        confirmButtonColor: '#dc3545',
      });
    }
  });

  const handleConfigurePoints = (store: StoreType) => {
    setPointsStore(store);
    setPointsSettings({
      points_per_currency: store.points_per_currency ?? 1,
      min_purchase_amount: store.min_purchase_amount ?? null,
      points_rounding: store.points_rounding ?? 'round',
    });
    setShowPointsModal(true);
    setActiveDropdown(null);
  };

  const handlePointsSettingsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pointsStore) return;
    pointsSettingsMutation.mutate({ storeId: pointsStore.id, data: pointsSettings });
  };

  const handleDelete = async (store: StoreType) => {
    const result = await Swal.fire({
      title: '<strong>Confirmar Exclusão</strong>',
      html: `Tem certeza que deseja excluir a loja <b>${store.name}</b>?<br/><small style="color: #dc3545;">Esta ação não pode ser desfeita.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="fas fa-trash"></i> Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      customClass: {
        popup: 'animated fadeInDown',
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary'
      },
      buttonsStyling: false,
      width: '500px'
    });

    if (result.isConfirmed) {
      deleteMutation.mutate(store.id);
    }
    setActiveDropdown(null);
  };

  const handleEdit = (store: StoreType) => {
    setSelectedStore(store);
    setModalMode('edit');
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleViewDetails = (store: StoreType) => {
    setSelectedStore(store);
    setModalMode('view');
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleNewStore = () => {
    setSelectedStore(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleExport = () => {
    if (!stores || stores.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nenhum dado',
        text: 'Não há lojas para exportar',
        confirmButtonColor: '#00d4aa'
      });
      return;
    }

    // Criar CSV
    const headers = ['ID', 'Nome', 'CNPJ', 'Telefone', 'Email', 'Status', 'Cidade'];
    const rows = stores.map(store => [
      store.id,
      store.name,
      store.cnpj,
      store.phone || '',
      store.email || '',
      store.is_active ? 'Ativa' : 'Inativa',
      store.address?.city || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lojas_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Exportado!',
      text: 'Arquivo CSV baixado com sucesso',
      showConfirmButton: false,
      timer: 2000
    });
  };

  const handleActionClick = (storeId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === storeId ? null : storeId);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStore(null);
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const data: any = {
      name: formData.get('name') as string,
      address: formData.get('address') as string,
      phone: formData.get('phone') as string,
      plan_id: 1 // Plano padrão
    };

    try {
      let response;

      if (modalMode === 'create') {
        response = await apiService.createStore(data);
      } else if (selectedStore) {
        response = await apiService.updateStore(selectedStore.id, data);
      }

      if (!response?.success) {
        throw new Error(response?.message || 'Erro ao salvar loja');
      }

      // Fecha modal imediatamente e dispara refetch em background
      setShowModal(false);
      setSelectedStore(null);
      setModalMode('create');
      void queryClient.invalidateQueries({ queryKey: ['stores'] });

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: modalMode === 'create' ? 'Loja criada com sucesso' : 'Loja atualizada com sucesso',
        confirmButtonColor: '#00d4aa',
        timer: 2000
      });
    } catch (error: any) {
      console.error('Error saving store:', error);
      setShowModal(false);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || error.response?.data?.message || 'Erro ao salvar loja',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fechar dropdown quando clicar fora
  React.useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Paginação
  const totalPages = stores ? Math.ceil(stores.length / ITEMS_PER_PAGE) : 0;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedStores = stores?.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
        </Header>
        <LoadingContainer>Carregando lojas...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
        </Header>
        <ErrorContainer>
          Erro ao carregar lojas. Tente novamente.
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
      </Header>

      <HeaderContent>
        <Title>Gerenciar Lojas ({stores?.length || 0})</Title>
        <HeaderActions>
          <Button $variant="outline" onClick={handleExport}>
            <Download size={16} />
            Exportar
          </Button>
          <Button $variant="primary" onClick={handleNewStore}>
            <Plus size={16} />
            Nova Loja
          </Button>
        </HeaderActions>
      </HeaderContent>

      {stores && stores.length === 0 ? (
        <EmptyState>
          Nenhuma loja cadastrada ainda.
        </EmptyState>
      ) : (
        <TableContainer>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Loja</Th>
                  <Th>Contato</Th>
                  <Th>Localização</Th>
                  <Th>Status</Th>
                  <Th style={{ width: '80px' }}>Ações</Th>
                </tr>
              </thead>
              <tbody>
                {paginatedStores?.map((store: StoreType) => (
                  <tr key={store.id}>
                    <Td>
                      <StoreName>{store.name}</StoreName>
                      <StoreInfo>{store.description || 'Sem descrição'}</StoreInfo>
                    </Td>
                    <Td>
                      <div>{store.phone || '-'}</div>
                      <StoreInfo>{store.email || '-'}</StoreInfo>
                    </Td>
                    <Td>
                      {store.address
                        ? (typeof store.address === 'string'
                            ? store.address
                            : `${store.address.city || ''}${store.address.city && store.address.state ? ', ' : ''}${store.address.state || ''}` || '-')
                        : '-'}
                    </Td>
                    <Td>
                      <StatusBadge $active={store.is_active}>
                        {store.is_active ? 'Ativa' : 'Inativa'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <ActionMenu>
                        <ActionButton onClick={(e) => handleActionClick(store.id, e)}>
                          <MoreHorizontal size={16} />
                        </ActionButton>
                        <DropdownMenu $show={activeDropdown === store.id}>
                          <DropdownItem onClick={() => handleViewDetails(store)}>
                            <Eye size={14} />
                            Ver Detalhes
                          </DropdownItem>
                          <DropdownItem onClick={() => handleEdit(store)}>
                            <Edit size={14} />
                            Editar
                          </DropdownItem>
                          <DropdownItem onClick={() => handleConfigurePoints(store)}>
                            <Settings size={14} />
                            Configurar Pontos
                          </DropdownItem>
                          <DropdownItem className="danger" onClick={() => handleDelete(store)}>
                            <Trash2 size={14} />
                            Excluir
                          </DropdownItem>
                        </DropdownMenu>
                      </ActionMenu>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          {totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </PageButton>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <PageButton
                  key={page}
                  $active={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PageButton>
              ))}

              <PageButton
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Próxima
              </PageButton>
            </Pagination>
          )}
        </TableContainer>
      )}

      {/* Modal: Configurar Pontos */}
      <Modal $show={showPointsModal} onClick={() => setShowPointsModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Configurar Pontos</ModalTitle>
            <ModalSubtitle>{pointsStore?.name}</ModalSubtitle>
          </ModalHeader>
          <Form onSubmit={handlePointsSettingsSubmit}>
            <FormGroup>
              <Label>Pontos por R$ 1,00</Label>
              <Input
                type="number"
                step="0.0001"
                min="0.0001"
                max="1000"
                value={pointsSettings.points_per_currency}
                onChange={(e) => setPointsSettings({ ...pointsSettings, points_per_currency: parseFloat(e.target.value) || 1 })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Valor mínimo de compra (R$) — opcional</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pointsSettings.min_purchase_amount ?? ''}
                onChange={(e) => setPointsSettings({ ...pointsSettings, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="Sem mínimo"
              />
            </FormGroup>
            <FormGroup>
              <Label>Arredondamento</Label>
              <SelectInput
                value={pointsSettings.points_rounding}
                onChange={(e) => setPointsSettings({ ...pointsSettings, points_rounding: e.target.value as 'floor' | 'round' | 'ceil' })}
              >
                <option value="round">Arredondar (padrão)</option>
                <option value="floor">Truncar (para baixo)</option>
                <option value="ceil">Arredondar para cima</option>
              </SelectInput>
            </FormGroup>
            <FormActions>
              <Button type="button" onClick={() => setShowPointsModal(false)}>
                Cancelar
              </Button>
              <Button $variant="primary" type="submit" disabled={pointsSettingsMutation.isPending}>
                {pointsSettingsMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>

      <Modal $show={showModal} onClick={handleCloseModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {modalMode === 'create' && 'Nova Loja'}
              {modalMode === 'edit' && 'Editar Loja'}
              {modalMode === 'view' && 'Detalhes da Loja'}
            </ModalTitle>
            <ModalSubtitle>
              {modalMode === 'create' && 'Preencha os dados da nova loja'}
              {modalMode === 'edit' && 'Atualize as informações da loja'}
              {modalMode === 'view' && 'Informações completas da loja'}
            </ModalSubtitle>
          </ModalHeader>

          {modalMode === 'view' && selectedStore ? (
            <>
              <FormGroup>
                <Label>Nome</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  {selectedStore.name}
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Descrição</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  {selectedStore.description || 'Sem descrição'}
                </div>
              </FormGroup>
              <FormActions>
                <Button type="button" onClick={handleCloseModal}>
                  Fechar
                </Button>
              </FormActions>
            </>
          ) : (
            <Form onSubmit={handleSubmitForm}>
              <FormGroup>
                <Label>Nome da Loja *</Label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Ex: Loja Centro"
                  defaultValue={selectedStore?.name}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Endereço</Label>
                <Input
                  type="text"
                  name="address"
                  placeholder="Rua, Número, Bairro"
                  defaultValue={typeof selectedStore?.address === 'string' ? selectedStore?.address : ''}
                />
              </FormGroup>

              <FormGroup>
                <Label>Telefone</Label>
                <Input
                  type="text"
                  name="phone"
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  defaultValue={selectedStore?.phone}
                  onChange={(e) => {
                    const numbers = e.target.value.replace(/\D/g, '');
                    let formatted = '';
                    if (numbers.length <= 2) formatted = numbers;
                    else if (numbers.length <= 7) formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
                    else formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
                    e.target.value = formatted;
                  }}
                />
              </FormGroup>

              <FormActions>
                <Button type="button" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button $variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : modalMode === 'create' ? 'Criar Loja' : 'Salvar Alterações'}
                </Button>
              </FormActions>
            </Form>
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
};
